import { BaseRateTypes, Guid } from '../models';
import { FormikHelpers } from 'formik';

export const ApiRootUrl = process.env.REACT_APP_CEREBRO_API_ROOT_URL ?? '';

export function SetFieldErrors(
	apiErrors: ApiErrors,
	setFieldError: FormikHelpers<unknown>['setFieldError'],
	convertToCamelCase: boolean = false
): void {
	for (var field in apiErrors) {
		const fieldName = convertToCamelCase ? field[0].toLowerCase() + field.substring(1) : field;
		setFieldError(fieldName, apiErrors[field][0]);
	}
}

export interface ApiErrors {
	[key: string]: string[];
}

export interface CerebroNotFound {
	/**
	 * The name of the object that was not found.
	 */
	objectNotFound: string;

	/**
	 * A message explaining why the object was not found.
	 */
	message: string;
}

export function IsCerebroNotFound(value: any): value is CerebroNotFound {
	return value?.objectNotFound !== undefined && value?.message !== undefined;
}

export const CerebroApiEndpoints = {
	Users: {
		Current: `/Api/Users/Current`,
		IsDataRoomTeamMember: (dataRoomGuid: Guid | null) => `/Api/Users/Current/${dataRoomGuid}/IsTeamMember`,
		TransactionManagers: (userGuid: Guid) => `/Api/Users/GetTransactionManagers/${userGuid}`,
		LenderRelationsManagers: (userGuid: Guid) => `/Api/Users/GetLenderRelationsManagers/${userGuid}`,
		SalesReps: (userGuid: Guid) => `/Api/Users/GetSalesReps/${userGuid}`,
		ControlledObjects: (userGuid: Guid | undefined) => `/Api/Users/${userGuid}/ControlledObjects`,
		Unlock: (userGuid: Guid | undefined) => `/Api/Users/${userGuid}/Unlock`,
	},

	TeamMembers: {
		Specific: (dataRoomGuid: Guid | undefined, teamMemberGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/TeamMembers/${teamMemberGuid}`,
		AssociatedSpecific: (dataRoomGuid: Guid | undefined, teamMemberGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/TeamMembers/${teamMemberGuid}/AssociatedContact`,
		Invite: (dataRoomGuid: Guid | undefined, teamMemberGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/TeamMembers/${teamMemberGuid}/Invite`,
	},
	ExcludedLenders: {
		Root: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/ExcludedLenders`,
		Specific: (dealGuid: Guid | undefined, excludedLenderGuid: Guid | undefined) =>
			`/Api/Deals/${dealGuid}/ExcludedLenders/${excludedLenderGuid}`,
	},
	DataRooms: {
		Root: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}`,
		Confirmation: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Confirmations`,
		LaunchDeal: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/LaunchDeal`,
		LoanApplication: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/LoanApplication`,
		Summary: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Summary`,
		Folders: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Folders`,
		FolderHierarchy: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Folders/Hierarchy`,
		SendFolderLink: (dataRoomGuid: Guid | undefined, folderGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/SendRepositoryLink/${folderGuid}/Folder`,
		SendDocumentLink: (dataRoomGuid: Guid | undefined, documentGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/SendRepositoryLink/${documentGuid}/Document`,
		SendLink: (dataRoomGuid: Guid | undefined, linkGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/SendRepositoryLink/${linkGuid}`,
		AccessLogs: (dataRoomGuid: Guid | undefined, userGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/AccessLogs/${userGuid}`,
		ActivityLogs: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/ActivityLogs`,
		CreateDataRoomAccessedLog: (dataRoomGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/CreateDataRoomAccessedLog`,
		DownloadRequests: (dataRoomGuid: Guid | undefined, userGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/DownloadRequests/${userGuid}`,
		OtherActiveDeals: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/OtherActiveDeals`,
		CopyDocuments: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/CopyDocuments`,
		LenderSummaryReport: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/LenderSummaryReport`,
		ActiveLenderSummaryReport: (dataRoomGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/ActiveLenderSummaryReport`,
		DownloadActiveLenderSummaryReport: (dataRoomGuid: Guid | undefined) =>
			`${ApiRootUrl}/Api/DataRooms/${dataRoomGuid}/ActiveLenderSummaryReport/Download`,
		DownloadLenderSummaryReport: (dataRoomGuid: Guid | undefined) =>
			`${ApiRootUrl}/Api/DataRooms/${dataRoomGuid}/LenderSummaryReport/Download`,
		LenderStatusBar: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/LenderStatusBar`,
	},

	Nda: {
		Standard: (dataRoomGuid: string, companyName: string) =>
			`/Api/DataRooms/${dataRoomGuid}/Nda/Standard/${encodeURIComponent(companyName)}`,
	},

	LenderNda: {
		Root: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Nda`,
		Signed: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Nda/Signed`,
		Download: (dataRoomGuid: Guid | undefined, userGuid: Guid | undefined) =>
			`${ApiRootUrl}/Api/DataRooms/${dataRoomGuid}/Nda/${userGuid}/Signed/Download`,
		Access: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Nda/Access`,
		Available: (dataRoomGuid: Guid | undefined, userGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Nda/${userGuid}/Available`,
	},

	Calls: {
		Root: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Calls`,
		Existing: (dataRoomGuid: Guid | undefined, dataRoomCallGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Calls/${dataRoomCallGuid}`,
		Lender: (dataRoomGuid: Guid | undefined, profileGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Calls/Lender/${profileGuid}`,
		Reset: (dataRoomGuid: Guid | undefined, dataRoomCallGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Calls/${dataRoomCallGuid}/Reset`,
	},

	TermSheets: {
		Add: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/TermSheets/Add`,
		AddExcludedLender: (dataRoomGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/AddExcludedLender`,
		Specific: (dataRoomGuid: Guid | undefined, termSheetGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}`,
		Revision: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined
		) => `/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}`,
		RevisionMatchingParams: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined
		) => `/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/DealMatchingParams`,
		AddRevision: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined
		) => `/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Add`,
		Revisions: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/TermSheets/LenderRevisions`,
		RevisionsByGuid: (dataRoomGuid: Guid | undefined, termSheetGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions`,
		AbsoluteDownload: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | undefined,
			revisionGuid: Guid | undefined
		) =>
			`${ApiRootUrl}/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Download`,
		List: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/TermSheets`,
		Upload: (dataRoomGuid: Guid | undefined, dataRoomUserGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/Lenders/${dataRoomUserGuid}/Upload`,
		ReplaceFile: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined
		) => `/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/ReplaceFile`,
		Comparison: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/TermSheets/Comparison`,
		ComparisonExcel: (dataRoomGuid: Guid | undefined) =>
			`${ApiRootUrl}/Api/DataRooms/${dataRoomGuid}/TermSheets/Comparison/Download`,
		Facility: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined
		) => `/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Facility`,
		FacilitySpecific: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			facilityGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Facility/${facilityGuid}`,
		FacilityFees: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			facilityGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Facility/${facilityGuid}/Fees`,
		SpecificFacilityFee: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			facilityGuid: Guid | null | undefined,
			feeGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Facility/${facilityGuid}/Fees/${feeGuid}`,
		FacilityCollateral: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			facilityGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Facility/${facilityGuid}/Collateral`,
		SpecificFacilityCollateral: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			facilityGuid: Guid | null | undefined,
			collateralGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Facility/${facilityGuid}/Collateral/${collateralGuid}`,
		Covenant: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined
		) => `/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Covenant`,
		PainPoint: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			facilityGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Facility/${facilityGuid}/PainPoint`,
		PainPointSpecific: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			facilityGuid: Guid | null | undefined,
			painPointGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Facility/${facilityGuid}/PainPoint/${painPointGuid}`,
		CovenantSpecific: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			covenantGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Covenant/${covenantGuid}`,
		Reporting: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined
		) => `/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Reporting`,
		ReportingSpecific: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			reportingGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Reporting/${reportingGuid}`,
		Conditions: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined
		) => `/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Conditions`,
		ConditionSpecific: (
			dataRoomGuid: Guid | undefined,
			termSheetGuid: Guid | null | undefined,
			revisionGuid: Guid | null | undefined,
			conditionGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/TermSheets/${termSheetGuid}/Revisions/${revisionGuid}/Conditions/${conditionGuid}`,
		HistoricalBankRate: (revisionGuid: Guid | null | undefined, rateType: BaseRateTypes | null | undefined) =>
			`/Api/DataRooms/GetBankRateHistoricalValue?revisionGuid=${revisionGuid}&bankRateType=${rateType}`,
	},

	Lenders: {
		BankDealProfile: (dataRoomGuid: Guid | undefined, dataRoomUserGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Lenders/${dataRoomUserGuid}/BankDealProfile`,
	},

	DataRoomUsers: {
		Root: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Lenders`,
		GetByUser: (dataRoomGuid: Guid | undefined, userGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Users/${userGuid}`,
		LenderByUser: (dataRoomGuid: Guid | undefined, userGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Lenders/${userGuid}`,
		TeamMemberByUser: (dataRoomGuid: Guid | undefined, userGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Lenders/${userGuid}/TeamMember`,
		TeamMembersByRoom: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/TeamMembers`,
		Specific: (dataRoomGuid: Guid | undefined, dataRoomUserGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Lenders/${dataRoomUserGuid}`,

		Notes: (dataRoomGuid: Guid | undefined, dataRoomUserGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Lenders/${dataRoomUserGuid}/Notes`,
		TeamMembers: (dataRoomGuid: Guid | undefined, dataRoomUserGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Lenders/${dataRoomUserGuid}/TeamMembers/Available`,
		PostTeamMember: (dataRoomGuid: Guid | undefined, dataRoomUserGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Lenders/${dataRoomUserGuid}/TeamMembers`,
		Associate: (
			dataRoomGuid: Guid | undefined,
			dataRoomUserGuid: Guid | null | undefined,
			teamMemberGuid: Guid | null | undefined
		) => `/Api/DataRooms/${dataRoomGuid}/Lenders/${dataRoomUserGuid}/TeamMembers/${teamMemberGuid}/Associate`,
		AssociateContact: (
			dataRoomGuid: Guid | undefined,
			dataRoomUserGuid: Guid | null | undefined,
			teamMemberGuid: Guid | null | undefined
		) =>
			`/Api/DataRooms/${dataRoomGuid}/Lenders/${dataRoomUserGuid}/TeamMembers/${teamMemberGuid}/AssociateContact`,
		OptOut: (dataRoomGuid: Guid | undefined, dataRoomUserGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Lenders/${dataRoomUserGuid}/OptOut`,
		OptOutDetail: (dataRoomGuid: Guid | undefined, dataRoomUserGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Lenders/${dataRoomUserGuid}/OptOut/Details`,
		Nda: (dataRoomGuid: Guid | undefined, dataRoomUserGuid: Guid | undefined | null) =>
			`/Api/DataRooms/${dataRoomGuid}/Lenders/${dataRoomUserGuid}/Nda`,
		NdaDownload: (dataRoomGuid: Guid | undefined, dataRoomUserGuid: Guid | undefined) =>
			`${ApiRootUrl}/Api/DataRooms/${dataRoomGuid}/Lenders/${dataRoomUserGuid}/Nda/Download`,
		Invite: (dataRoomGuid: Guid | undefined, dataRoomUserGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Lenders/${dataRoomUserGuid}/Invite`,
		Email: (dataRoomGuid: Guid | undefined, dataRoomUserGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Lenders/${dataRoomUserGuid}/Email`,
		EmailMany: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Lenders/Email`,
		Grant: (dataRoomGuid: Guid | undefined, dataRoomUserGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Lenders/${dataRoomUserGuid}/Grant`,
	},

	DataRoomDiligenceRequests: {
		Root: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/DiligenceRequests`,
		Lender: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/LenderDiligenceRequests`,
		Specific: (dataRoomGuid: Guid | undefined, requestGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/DiligenceRequests/${requestGuid}`,
		AssociateDocuments: (dataRoomGuid: Guid | undefined, requestGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/DiligenceRequests/${requestGuid}/AssociateDocuments`,
		Files: (dataRoomGuid: Guid | undefined, requestGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/DiligenceRequests/${requestGuid}/Files`,
		SpecificFile: (
			dataRoomGuid: Guid | undefined,
			requestGuid: Guid | null | undefined,
			fileGuid: Guid | null | undefined
		) => `/Api/DataRooms/${dataRoomGuid}/DiligenceRequests/${requestGuid}/Files/${fileGuid}`,
		SpecificFileDownload: (
			dataRoomGuid: Guid | undefined,
			requestGuid: Guid | null | undefined,
			fileGuid: Guid | null | undefined
		) => `${ApiRootUrl}/Api/DataRooms/${dataRoomGuid}/DiligenceRequests/${requestGuid}/Files/${fileGuid}/Download`,
		Links: (dataRoomGuid: Guid | undefined, requestGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/DiligenceRequests/${requestGuid}/Links`,
		SpecificLink: (
			dataRoomGuid: Guid | undefined,
			requestGuid: Guid | null | undefined,
			linkGuid: Guid | null | undefined
		) => `/Api/DataRooms/${dataRoomGuid}/DiligenceRequests/${requestGuid}/Links/${linkGuid}`,
		Uploads: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/DiligenceRequests/Uploads`,
		SpecificUpload: (dataRoomGuid: Guid | undefined, uploadGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/DiligenceRequests/Uploads/${uploadGuid}`,
	},

	DataRoomContacts: {
		Root: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/BorrowerContacts`,
		Associate: (dataRoomGuid: Guid | undefined, dataRoomContactGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/BorrowerContacts/${dataRoomContactGuid}/Associate`,
		Update: (dataRoomGuid: Guid | undefined, dataRoomContactGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/BorrowerContacts/${dataRoomContactGuid}`,
		Task: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/BorrowerContacts/task`,
		SendAccountInfo: (dataRoomGuid: Guid | undefined, personId: number | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/BorrowerContacts/${personId}/SendAccountInfo`,
	},

	DataRoomThirdPartyContacts: {
		Root: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/ThirdPartyContacts`,
		Associate: (dataRoomGuid: Guid | undefined, dataRoomContactGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/ThirdPartyContacts/${dataRoomContactGuid}/Associate`,
		Update: (dataRoomGuid: Guid | undefined, dataRoomContactGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/ThirdPartyContacts/${dataRoomContactGuid}`,
		Task: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/ThirdPartyContacts/task`,
		SendAccountInfo: (dataRoomGuid: Guid | undefined, personId: number | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/ThirdPartyContacts/${personId}/SendAccountInfo`,
	},

	DataRoomPartnerContacts: {
		Root: (dealPartnerGuid: Guid | undefined) => `/Api/DealPartners/${dealPartnerGuid}/Contacts`,
		Contacts: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/PartnerContacts`,
		Update: (dataRoomGuid: Guid | undefined, contactGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/PartnerContacts/${contactGuid}`,
		SendAccountInfo: (dataRoomGuid: Guid | undefined, contactGuid: string | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/PartnerContacts/${contactGuid}/Invite`,
		RemoveAccess: (dataRoomGuid: Guid | undefined, contactGuid: string | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/PartnerContacts/${contactGuid}/Associate`,
		Associate: (dataRoomGuid: Guid | undefined, contactGuid: string | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/PartnerContacts/${contactGuid}/Associate`,
	},

	Documents: {
		Root: (dataRoomGuid: Guid | undefined, folderGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Documents/Folder/${folderGuid}`,
		Folder: (dataRoomGuid: Guid | undefined, parentFolderGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Folders/${parentFolderGuid}`,
		FolderDownload: (dataRoomGuid: Guid | undefined, folderGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Folders/${folderGuid}/Download`,
		FolderFileMove: (dataRoomGuid: Guid | undefined, folderGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Folders/${folderGuid}/MoveFiles`,
		DocumentRequest: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/DocumentDownloadRequest`,
		Update: (dataRoomGuid: Guid | undefined, documentGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Documents/${documentGuid}`,
		CopyTo: (dataRoomGuid: Guid | undefined, documentGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Documents/${documentGuid}/CopyTo`,
		Specific: (dataRoomGuid: Guid | undefined, documentGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Documents/${documentGuid}`,
		AbsoluteDownload: (dataRoomGuid: Guid | undefined, documentGuid: Guid | undefined) =>
			`${ApiRootUrl}/Api/DataRooms/${dataRoomGuid}/Documents/${documentGuid}/Download`,
		History: (dataRoomGuid: Guid | undefined, documentGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Documents/${documentGuid}/History`,
		Deleted: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Documents/Deleted`,
		All: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Documents/All`,
		Upload: (dataRoomGuid: Guid | undefined, folderGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Folders/$${folderGuid}/Upload`,
		DownloadRequest: (
			dataRoomGuid: Guid | undefined,
			userGuid: Guid | undefined,
			requestGuid: Guid | null | undefined
		) => `/Api/DataRooms/${dataRoomGuid}/DownloadRequests/${userGuid}/File/${requestGuid}`,
		AbsoluteDownloadRequest: (
			dataRoomGuid: Guid | undefined,
			userGuid: Guid | undefined,
			requestGuid: Guid | null | undefined
		) => `${ApiRootUrl}/Api/DataRooms/${dataRoomGuid}/DownloadRequests/${userGuid}/File/${requestGuid}`,
	},

	SuggestedLenders: {
		Root: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/SuggestedLenders`,
		Prompt: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/SuggestedLenders/Prompt`,
	},

	Deals: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}`,
	RelatedDeals: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/Related`,
	RelatedDownload: (dealGuid: Guid | undefined) => `${ApiRootUrl}/Api/Deals/${dealGuid}/Related/Download`,
	RelatedDealsReport: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/Related/Report`,
	Allocation: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/Allocation`,
	AllocationRealEstates: {
		Root: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/Allocation/RealEstates`,
	},
	DebtSchedules: {
		Root: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/DebtSchedules`,
	},

	LenderList: {
		Root: (dealGuid: Guid | undefined, structure?: number | null) =>
			`/Api/Deals/${dealGuid}/LenderList?structure=${structure}`,
	},

	ParticipantList: {
		Root: (dealGuid: Guid | undefined, structure?: number | null) =>
			`/Api/Deals/${dealGuid}/LenderParticipant?structure=${structure}`,
	},

	LenderInvites: {
		Root: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/LenderInvites`,
	},

	Teasers: {
		Root: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Teasers`,
		Matched: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Teasers/AddMatched`,
		Specific: (dataRoomGuid: Guid | undefined, teaserGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Teasers/${teaserGuid}`,
		IgnoreAtLaunch: (dataRoomGuid: Guid | undefined, teaserGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Teasers/${teaserGuid}/Ignore`,
		IntroEmail: (dataRoomGuid: Guid | undefined, teaserGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Teasers/${teaserGuid}/IntroEmail`,
		Reset: (dataRoomGuid: Guid | undefined, teaserGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Teasers/${teaserGuid}/Reset`,
		ReferralLanguage: (dataRoomGuid: Guid | undefined, teaserGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Teasers/${teaserGuid}/ReferralLanguage`,
		Preview: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/TeaserPreview`,
		Files: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/TeaserFiles`,
		FileDelete: (dataRoomGuid: Guid | undefined, fileGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/TeaserFiles/${fileGuid}`,
		AbsoluteDownload: (dataRoomGuid: Guid | undefined, fileGuid: Guid | undefined) =>
			`${ApiRootUrl}/Api/DataRooms/${dataRoomGuid}/TeaserFiles/${fileGuid}`,
		Upload: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/TeaserFiles/Upload`,
		Send: (dataRoomGuid: Guid | undefined, teaserGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Teasers/${teaserGuid}/Send`,
		SendMany: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Teasers/SendMany`,
		DeleteMany: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Teasers/DeleteMany`,
		IgnoreAtLaunchMany: (dataRoomGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Teasers/IgnoreAtLaunchMany`,
		IncludeAtLaunchMany: (dataRoomGuid: Guid | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Teasers/IncludeAtLaunchMany`,
	},

	Milestones: {
		Root: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Milestones`,
		LenderReport: (dataRoomGuid: Guid | undefined, bankContactGuid: Guid | null | undefined) =>
			`/Api/DataRooms/${dataRoomGuid}/Milestones/${bankContactGuid}`,
		DirectConnect: {
			Root: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Milestones/DirectConnect`,
			LenderReport: (dataRoomGuid: Guid | undefined, bankContactGuid: Guid | null | undefined) =>
				`/Api/DataRooms/${dataRoomGuid}/Milestones/DirectConnect/${bankContactGuid}`,
			UpdateLenderMilestone: (dataRoomGuid: Guid | undefined, bankContactGuid: Guid | null | undefined) =>
				`/Api/DataRooms/${dataRoomGuid}/Milestones/DirectConnect/${bankContactGuid}`,
			ContactBorrower: (dataRoomGuid: Guid | undefined, bankContactGuid: Guid | null | undefined) =>
				`/Api/DataRooms/${dataRoomGuid}/Milestones/DirectConnect/${bankContactGuid}/ContactBorrower`,
		},
	},

	Matching: {
		Get: (dealGuid: Guid | undefined) => `/Api/Matching/${dealGuid}`,

		Stats: (dealGuid: Guid | undefined) => `/Api/Matching/${dealGuid}/Stats`,

		Tranches: (dealGuid: Guid | undefined) => `/Api/Matching/${dealGuid}/Tranches`,

		DealTypePreferences: (dealGuid: Guid | undefined) => `/Api/Matching/${dealGuid}/DealTypePreferences`,

		OutputAnalysis: (dealGuid: Guid | undefined) => `/Api/Matching/${dealGuid}/OutputAnalysis`,

		Params: (dealGuid: Guid | undefined) => `/Api/Matching/${dealGuid}/Params`,

		Processing: (dealGuid: Guid | undefined) => `/Api/Matching/${dealGuid}/IsProcessing`,

		Lenders: (
			dealGuid?: string,
			mode?: number,
			tier1?: number,
			finderFee?: boolean,
			structure?: number | null,
			filterType?: number,
			matchMode?: number | null
		) =>
			structure != null
				? `/Api/Matching/${dealGuid}/Lenders?mode=${mode}&matchMode=${
						matchMode ?? 1
				  }&tier1=${tier1}&finderFee=${finderFee}&structure=${structure}&filterType=${filterType}`
				: `/Api/Matching/${dealGuid}/Lenders?mode=${mode}&matchMode=${
						matchMode ?? 1
				  }&tier1=${tier1}&finderFee=${finderFee}&filterType=${filterType}`,

		TermSheets: (dealGuid?: string, structure?: number | null, tier1?: number | null) =>
			`/Api/Matching/${dealGuid}/TermSheets?structure=${structure}&tier1=${tier1}`,

		Search: (
			dealGuid?: string,
			keywords?: string,
			tier1?: number | null,
			tier3?: number | null,
			mode?: number | null,
			geographyMode?: number | null
		) =>
			`/Api/Matching/${dealGuid}/Search?keywords=${keywords}&mode=${
				mode ?? 1
			}&geographyMode=${geographyMode}&tier1=${tier1 ?? ''}&tier3=${tier3 ?? ''}`,
		OtherDeals: (dealGuid: Guid | undefined) => `/Api/Matching/${dealGuid}/DealSearch`,
		AddToTeasers: (dealGuid: Guid | undefined, structure?: number | null) =>
			`/Api/Matching/${dealGuid}/AddToTeaser?structure=${structure}`,
		AddToDataRoom: (dealGuid: Guid | undefined, structure?: number | null) =>
			`/Api/Matching/${dealGuid}/AddToDataRoom?structure=${structure}`,
		UnavailableReasons: (dealGuid?: string, structureTranche?: number | null, tier1BankType?: number | null) =>
			`/Api/Matching/${dealGuid}/Unavailable?structure=${structureTranche}&tier1=${tier1BankType}`,
	},

	Company: (companyGuid: Guid | undefined) => `/Api/Companies/${companyGuid}`,

	CompanyContacts: {
		Root: (companyGuid: Guid | undefined) => `/Api/CompanyContacts/${companyGuid}`,
		FromDataRoom: (dataRoomGuid: Guid | undefined, isThirdParty: boolean) =>
			`/Api/CompanyContacts/DataRoom/${dataRoomGuid}?isThirdParty=${isThirdParty}`,
		BorrowerParticipants: (dataRoomGuid: Guid | undefined) => `/Api/CompanyContacts/DataRoom/${dataRoomGuid}`,
		SingleForDataRoom: (dataRoomGuid: Guid | undefined) => `/Api/CompanyContacts/DataRoom/${dataRoomGuid}/Contact`,
		UpdateExistingDataRoomAccess: (companyGuid: Guid | undefined, contactGuid: Guid | undefined) =>
			`/Api/CompanyContacts/${companyGuid}/UpdateExistingDataRoomAccess/${contactGuid}`,
		PasswordResetLink: (companyGuid: Guid | undefined, contactGuid: Guid | undefined) =>
			`/Api/CompanyContacts/${companyGuid}/GetPasswordResetLink/${contactGuid}`,
		AddExistingDataRoomAccess: (companyGuid: Guid | undefined) =>
			`/Api/CompanyContacts/${companyGuid}/AddExistingDataRoomAccess`,
		AddContactAndDataRoomAccess: (companyGuid: Guid | undefined, dataRoomGuid: Guid | undefined) =>
			`/Api/CompanyContacts/${companyGuid}/AddContactAndDataRoomAccess/${dataRoomGuid}`,
	},

	Product: (dealGuid: Guid | undefined) => `/Api/Deals/${dealGuid}/Product`,

	Lookups: {
		LenderDeclineReasons: `/Api/DeclineReasons`,
		ActiveDataRoomUsers: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Users/Active`,
		ActiveDataRoomLenders: (dataRoomGuid: Guid | undefined) => `/Api/DataRooms/${dataRoomGuid}/Lenders/Active`,
		FinancialCovenantTypes: `/Api/FinancialCovenantTypes`,
		Timezones: `/Api/Timezone`,
		TermSheetFees: `/Api/TermSheetFees`,
		ConditionTypes: `/Api/ConditionTypes`,
		ApplicationSettings: `/Api/Settings/All`,
	},

	Banks: {
		ActionsTaken: (bankGuid: Guid | undefined) => `/Api/Banks/${bankGuid}/ActionsTaken`,
		BankDealProfile: (bankGuid: Guid | undefined, bankContactGuid: Guid | null | undefined) =>
			`/Api/Banks/${bankGuid}/BankContacts/${bankContactGuid}/BankDealProfile`,
	},

	Integrations: {
		Config: `/Api/Integrations/Config`,
	},

	/**
	 * Builds an absolute version of an endpoint url
	 * @param endpoint An endpoint url string, preferably from this same object.
	 * @returns The endpoint string concatenated after the ApiRootUrll
	 */
	Absolute: (endpoint: string) => `${ApiRootUrl}${endpoint}`,
};

export function toQueryParams(paramName: string, values: string[]) {
	const queryParamKey = `&${paramName}=`;
	return queryParamKey + values.join(queryParamKey);
}
